import { useMemo, useCallback } from 'react'
import { useResizeDetector } from 'react-resize-detector'

import { NenreiStaticticsRecord } from './data'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components'

import {
  Box, Alert,
  Stack, Typography,
  useTheme,
  useMediaQuery,
  BoxProps
} from "@mui/material"

import { SisetuDoctorsConditions } from '../condition'

type BarItemStyle = {
  opacity?: number
  color?: string
}
const selectedStyle: BarItemStyle = {
}
const notSelectedStyle: BarItemStyle = {
  opacity: 0.3
}
const maleStyle: BarItemStyle = {
  color: '#73c0de'
}
const femaleStyle: BarItemStyle = {
  color: '#ee6666'
}

type SeriesData = {
  name: string
  age: number
  seibetu: {
    code: string
    name: string
  }
  value: number
  itemStyle?: BarItemStyle

}
type AgeStatistics = {
  startAge: number
  lastAge: number
  maxCount: number
  ages: number[]
  series: {
    male: SeriesData[]
    female: SeriesData[]
  }
}

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  CanvasRenderer
])

type NenreiHistgramProps = {
  sisetuCode: string
  dataList?: NenreiStaticticsRecord[]
  condition: SisetuDoctorsConditions
  onChangeCondition: (axisKey: 'nenrei', condition: {
    start: number
    last: number
    seibetu: string
    name: string
  }) => void
} & BoxProps
export const NenreiHistgram = ({
  dataList,
  condition,
  onChangeCondition,
  ...others
}: NenreiHistgramProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down('sm'))
  const { width, height, ref } = useResizeDetector()

  const ageStatistics = useMemo<AgeStatistics | null>(
    () => {
      if (!dataList) return null

      let start= 0, last = 0, max = 0
      dataList.forEach((record) => {
        if (record.nenrei === null) return
        if (start === 0 || start > record.nenrei) start = record.nenrei
        if (last === 0 || last < record.nenrei) last = record.nenrei
        if (max < record.doctorsCount) max = record.doctorsCount
      })
      if (!start || !last || !max) return null

      start = Math.floor(start / 10)
      last = Math.floor(last / 10)

      const ages = Array.from(new Array((last - start) + 1), (x, i) => (i + start) * 10)
      const isRange = (age: number, seibetu: '1' | '2', r: NenreiStaticticsRecord) => 
        (r.nenrei >= age && r.nenrei < (age + 10) && r.seibetuCode === seibetu)
      const male = ages.map((age) => ({
        name: `${age}代 男性`,
        age: age,
        seibetu: { code: '1', name: '男性' },
        value: dataList.reduce((p, r) => p + (isRange(age, '1', r) ? r.doctorsCount : 0), 0),
        itemStyle: condition.nenrei ? (
          (condition.nenrei.start === age && condition.nenrei.seibetu === '1') ? selectedStyle : notSelectedStyle
        ) : maleStyle
      }))
      const female = ages.map((age) => ({
        name: `${age}代 女性`,
        age: age,
        seibetu: { code: '2', name: '女性' },
        value: dataList.reduce((p, r) => p + (isRange(age, '2', r) ? r.doctorsCount : 0), 0),
        itemStyle: condition.nenrei ? (
          (condition.nenrei.start === age && condition.nenrei.seibetu === '2') ? selectedStyle : notSelectedStyle
        ) : femaleStyle
      }))
      return {
        startAge: start * 10,
        lastAge: last * 10,
        maxCount: max,
        ages,
        series: { male, female }
      }
    }, 
    [dataList, condition.nenrei]
  )

  const tooltipPosition = useCallback(
    (point: number[]) => [point[0], '10%'],
    []
  )
  const valueFormatter = useCallback(
    (value: number | string) => `${value}人`,
    []
  )
  const barData = useCallback(
    (label: string, data: SeriesData[]) => ({
      name: label,
      type: 'bar',
      barGap: 0,
      barCategoryGap: isSmallMedia ? 5 : 20,
      label: {
        show: true,
        formatter: `{b}\n{c}人`,
        position: 'top'
      },
      data: data,
      itemStyle: {
        borderRadius: [3, 3, 0, 0]
      }
    }),
    [isSmallMedia]
  )

  const option = useMemo(
    () => ({
      //animation: false,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        enterable: false,
        position: tooltipPosition,
        valueFormatter
      },
      grid: {
        left: 8, right: 8,
        top: 32, bottom: 8
      },
      xAxis: {
        type: 'category',
        show: false,
        data: ageStatistics?.ages.map((age) => `${age}代`) || []
      },
      yAxis: {
        type: 'value',
        show: false
      },
      series: [
        barData('男性', ageStatistics?.series.male || []),
        barData('女性', ageStatistics?.series.female || [])
      ],
      textStyle: {
        fontFamily: 'Noto Sans JP,sans-serif'
      }
    }),
    [barData, ageStatistics, tooltipPosition, valueFormatter]
  )

  const onClickBarChart = useCallback(
    (params: { data?: { age: number, seibetu: { code: string, name: string } }}) => {
      if (!params.data) return
      const ageStart = params.data.age
      const seibetu = params.data.seibetu
      onChangeCondition('nenrei', {
        start: ageStart,
        last: ageStart + 10,
        seibetu: seibetu.code,
        name: `${ageStart}代 ${seibetu.name}`
      })
    },
    [onChangeCondition]
  )
  return (
    <Box ref={ref} width="100%" height="100%" position="relative" {...others}>
      <Stack direction="column" sx={{ position: 'absolute', zIndex: 2, boxSizing: 'border-box', width: '100%' }} padding={1} spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body1" component="div" color="text.primary" sx={{ textAlign: 'left', flexGrow: 1 }}>年代 / 性別</Typography>
        </Stack>
        { dataList?.length === 0 && <Alert severity="info">対象が見つかりません.</Alert> }
      </Stack>

      {width && height && (
        <ReactEChartsCore
          echarts={echarts}
          notMerge={true}
          onEvents={{
            'click': onClickBarChart
          }}
          option={option}
          opts={{
            renderer: 'canvas',
          }}
          style={{
            width,
            height
          }}
          theme="insite"
        />
      )}
    </Box>
  )
}